import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Calendar, User, Clock, ArrowLeft, ChevronRight, Facebook, Twitter,
  Linkedin, Mail, Link2, Eye, List, X, ChevronUp, Tag, ArrowRight,
  MessageSquare, BookOpen, Users, Globe, Send, AlertTriangle
} from 'lucide-react';
import { getPostById, getAllPosts } from '../services/firebaseBlogService';
import { seedPosts } from '../data/seedPosts';
import SEO from '../components/SEO';

const SITE_URL = 'https://qsconn.com';

const CATEGORY_COLORS = {
  'Manufacturing': '#dc2626',
  'Technology': '#2563eb',
  'Industry Insights': '#7c3aed',
  'Sourcing Guide': '#ea580c',
  'Quality': '#16a34a',
};

const getCategoryStyle = (category, theme) => {
  const color = CATEGORY_COLORS[category] || '#6b7280';
  return theme === 'dark'
    ? { backgroundColor: `${color}20`, color, borderColor: `${color}40` }
    : { backgroundColor: `${color}15`, color, borderColor: `${color}30` };
};

// Extract headings from HTML content for Table of Contents
const extractHeadings = (content) => {
  const headings = [];
  const regex = /<h([23])[^>]*>(.*?)<\/h\1>/gi;
  let match;
  while ((match = regex.exec(content)) !== null) {
    const level = parseInt(match[1]);
    const text = match[2].replace(/<[^>]*>/g, '');
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
    headings.push({ level, text, id });
  }
  return headings;
};

// Estimate word count from HTML content
const estimateWordCount = (content) => {
  const text = content.replace(/<[^>]*>/g, ' ');
  const words = text.trim().split(/\s+/).filter(w => w.length > 0);
  return words.length;
};

// Inject IDs into headings for anchor links
const injectHeadingIds = (content) => {
  return content.replace(/<h([23])([^>]*)>(.*?)<\/h\1>/gi, (match, level, attrs, text) => {
    const plainText = text.replace(/<[^>]*>/g, '');
    const id = plainText.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
    return `<h${level}${attrs} id="${id}">${text}</h${level}>`;
  });
};

// Scroll to heading smoothly
const scrollToHeading = (id) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// Author card component
const AuthorCard = ({ author, theme }) => (
  <div className={`rounded-2xl p-6 mb-8 ${
    theme === 'dark' ? 'bg-white/5 border border-white/5' : 'bg-gray-50 border border-gray-100'
  }`}>
    <div className="flex items-start gap-4">
      {/* Avatar */}
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 via-red-600 to-pink-600 flex items-center justify-center text-white text-xl font-extrabold flex-shrink-0 shadow-lg shadow-red-600/20">
        {author ? author.charAt(0).toUpperCase() : 'Q'}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className={`font-bold text-base mb-0.5 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          {author || 'Qingshun Engineering Team'}
        </h3>
        <p className={`text-xs text-red-600 font-semibold mb-2`}>
          Connector Manufacturing Experts
        </p>
        <p className={`text-xs leading-relaxed ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          Qingshun Electronics engineering team. 14+ years designing and manufacturing precision connectors for automotive, industrial, and consumer electronics applications.
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold text-red-600 hover:text-red-700 transition-colors"
        >
          <Mail className="w-3.5 h-3.5" /> Contact Our Team
        </Link>
      </div>
    </div>
  </div>
);

// Table of Contents sidebar
const TableOfContents = ({ headings, theme, activeId }) => {
  if (!headings || headings.length === 0) return null;

  return (
    <nav className={`rounded-2xl overflow-hidden ${
      theme === 'dark' ? 'bg-white/5 border border-white/5' : 'bg-gray-50 border border-gray-100'
    }`}>
      <div className={`px-5 py-4 font-bold text-xs uppercase tracking-widest flex items-center gap-2 ${
        theme === 'dark' ? 'text-gray-300 border-b border-white/5' : 'text-gray-700 border-b border-gray-200'
      }`}>
        <List className="w-4 h-4" /> Table of Contents
      </div>
      <ul className="p-4 space-y-1">
        {headings.map((heading, i) => (
          <li key={i}>
            <button
              onClick={() => scrollToHeading(heading.id)}
              className={`w-full text-left text-sm py-1.5 px-3 rounded-lg transition-all duration-200 ${
                heading.level === 3 ? 'pl-6' : ''
              } ${
                activeId === heading.id
                  ? theme === 'dark'
                    ? 'bg-red-900/30 text-red-400 font-semibold'
                    : 'bg-red-50 text-red-700 font-semibold'
                  : theme === 'dark'
                    ? 'text-gray-400 hover:text-white hover:bg-white/5'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <span className="line-clamp-2">{heading.text}</span>
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

// Related posts card
const RelatedPostCard = ({ post, theme }) => (
  <Link
    to={`/blog/${post.slug || post.id}`}
    className={`group block rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1 ${
      theme === 'dark'
        ? 'bg-white/5 border-white/5 hover:border-white/10 hover:bg-white/8'
        : 'bg-white border-gray-100 hover:border-gray-200 hover:shadow-md'
    }`}
  >
    {post.image && (
      <div className="aspect-video overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.target.src = 'https://picsum.photos/400/225?blur=2'; }}
        />
      </div>
    )}
    <div className="p-4">
      {post.category && (
        <span
          className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-2"
          style={getCategoryStyle(post.category, theme)}
        >
          {post.category}
        </span>
      )}
      <h4 className={`text-sm font-bold leading-snug line-clamp-2 group-hover:text-red-500 transition-colors ${
        theme === 'dark' ? 'text-white' : 'text-gray-900'
      }`}>
        {post.title}
      </h4>
      <div className={`flex items-center gap-3 mt-2 text-xs ${
        theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
      }`}>
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {post.date}
        </span>
        {post.readTime && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {post.readTime}
          </span>
        )}
      </div>
    </div>
  </Link>
);

// Share buttons
const ShareButtons = ({ url, title, theme }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareItems = [
    {
      label: 'Facebook',
      icon: <Facebook className="w-4 h-4" />,
      action: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank'),
      color: 'bg-blue-600 hover:bg-blue-700',
      lightColor: 'bg-blue-50 text-blue-600 border-blue-200',
    },
    {
      label: 'Twitter',
      icon: <Twitter className="w-4 h-4" />,
      action: () => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank'),
      color: 'bg-sky-500 hover:bg-sky-600',
      lightColor: 'bg-sky-50 text-sky-600 border-sky-200',
    },
    {
      label: 'LinkedIn',
      icon: <Linkedin className="w-4 h-4" />,
      action: () => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`, '_blank'),
      color: 'bg-blue-700 hover:bg-blue-800',
      lightColor: 'bg-blue-50 text-blue-800 border-blue-200',
    },
    {
      label: 'Copy Link',
      icon: copied ? <ChevronUp className="w-4 h-4" /> : <Link2 className="w-4 h-4" />,
      action: handleCopy,
      color: copied ? 'bg-green-600' : (theme === 'dark' ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-500 hover:bg-gray-600'),
      lightColor: copied ? 'bg-green-50 text-green-700 border-green-200' : (theme === 'dark' ? 'bg-white/5 text-gray-300 border-white/10' : 'bg-gray-100 text-gray-600 border-gray-200'),
    },
  ];

  return (
    <div className="flex flex-wrap gap-3">
      {shareItems.map((item) => (
        <button
          key={item.label}
          onClick={item.action}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 ${item.color}`}
        >
          {item.icon}
          <span className="hidden sm:inline">{item.label}</span>
        </button>
      ))}
    </div>
  );
};

// Callout box renderer
const renderCalloutBoxes = (content) => {
  // Replace blockquotes with styled callout boxes
  return content.replace(
    /<blockquote>([\s\S]*?)<\/blockquote>/gi,
    (match, inner) => {
      // Check if it contains common callout patterns
      const isTip = /tip|pro|advice|hint|recommend/i.test(inner);
      const isWarning = /warning|caution|注意|注意|danger|important/i.test(inner);
      const isNote = /note|remember|note that|note:/i.test(inner);

      let icon = '💡';
      let typeClass = 'callout-tip';
      if (isWarning) { icon = '⚠️'; typeClass = 'callout-warning'; }
      else if (isNote) { icon = '📋'; typeClass = 'callout-note'; }

      return `<div class="callout-box ${typeClass}"><div class="callout-icon">${icon}</div><div class="callout-content">${inner}</div></div>`;
    }
  );
};

const BlogPost = ({ theme }) => {
  const { id: slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [views, setViews] = useState(0);
  const [activeHeadingId, setActiveHeadingId] = useState('');
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchPost = async () => {
      setIsLoading(true);
      setError(null);

      const timeoutId = setTimeout(() => {
        if (isMounted) {
          setError('Connection timeout. Please check your internet connection.');
          setIsLoading(false);
        }
      }, 10000);

      try {
        const postData = await getPostById(slug);
        if (!isMounted) return;

        if (postData) {
          setPost(postData);
          setViews(postData.views || Math.floor(Math.random() * 500) + 100);

          // Load related posts
          const allPosts = await getAllPosts();
          if (isMounted) {
            const related = (allPosts || seedPosts)
              .filter(p => p.id !== postData.id && p.id !== postData.slug)
              .filter(p => p.category === postData.category || p.tags?.some(t => postData.tags?.includes(t)))
              .slice(0, 3);
            setRelatedPosts(related);
          }
        } else {
          // Try seed posts
          const seedPost = seedPosts.find(p => p.slug === slug || p.id === slug);
          if (seedPost) {
            setPost(seedPost);
            setViews(seedPost.views || Math.floor(Math.random() * 500) + 100);
            const related = seedPosts
              .filter(p => p.id !== seedPost.id && p.category === seedPost.category)
              .slice(0, 3);
            setRelatedPosts(related);
          } else {
            setError('Post not found');
          }
        }
      } catch (err) {
        console.error('Error fetching post:', err);
        if (isMounted) {
          const seedPost = seedPosts.find(p => p.slug === slug || p.id === slug);
          if (seedPost) {
            setPost(seedPost);
            setViews(seedPost.views || Math.floor(Math.random() * 500) + 100);
          } else {
            setError('Failed to load post. Please try again.');
          }
        }
      } finally {
        clearTimeout(timeoutId);
        if (isMounted) setIsLoading(false);
      }
    };

    if (slug) fetchPost();
    return () => { isMounted = false; };
  }, [slug]);

  // Scroll spy for active heading
  useEffect(() => {
    if (!post?.content) return;
    const headings = extractHeadings(post.content);
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveHeadingId(entry.target.id);
          }
        });
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    );

    headings.forEach(h => {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [post?.content]);

  const shareUrl = `${SITE_URL}/blog/${slug}`;
  const shareTitle = post?.title || 'Blog Post';
  const wordCount = post ? estimateWordCount(post.content) : 0;
  const headings = post ? extractHeadings(post.content) : [];
  const processedContent = post ? injectHeadingIds(renderCalloutBoxes(post.content)) : '';

  // Detect if content has FAQ or HowTo sections
  const hasFAQ = post?.content?.toLowerCase().includes('faq') ||
    post?.content?.toLowerCase().includes('frequently asked') ||
    post?.content?.toLowerCase().includes('<h3>') && post?.content?.toLowerCase().includes('?');

  // SEO Structured Data
  const blogPostSchema = post ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.seoDescription || post.excerpt,
    "image": post.image ? [post.image] : [],
    "datePublished": post.date,
    "dateModified": post.updatedAt || post.date,
    "author": {
      "@type": "Organization",
      "name": post.author || "Qingshun Electronics",
      "url": `${SITE_URL}/about`,
    },
    "publisher": {
      "@type": "Organization",
      "name": "Qingshun Electronics",
      "url": SITE_URL,
      "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo512.png` }
    },
    "url": shareUrl,
    "mainEntityOfPage": { "@type": "WebPage", "@id": shareUrl },
    "wordCount": wordCount,
    "timeRequired": post.readTime || "PT10M",
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": [".article-headline", ".article-excerpt"],
      "xpath": ["/html/head/title", "/html/head/meta[@name='description']/@content"]
    },
    ...(hasFAQ && {
      "hasPart": {
        "@type": "WebPageElement",
        "isPartOf": { "@type": "CreativeWork", "name": post.title },
        "cssSelector": ".faq-section"
      }
    })
  } : null;

  const faqSchema = hasFAQ && post ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": (post.content.match(/<h3[^>]*>(.*?)<\/h3>[\s\S]*?<p>(.*?)<\/p>/gi) || []).slice(0, 5).map(match => {
      const qMatch = match.match(/<h3[^>]*>(.*?)<\/h3>/i);
      const aMatch = match.match(/<p>([\s\S]*?)<\/p>/i);
      const question = qMatch ? qMatch[1].replace(/<[^>]*>/g, '').trim() : '';
      const answer = aMatch ? aMatch[1].replace(/<[^>]*>/g, ' ').trim() : '';
      if (question && answer && question.includes('?')) {
        return {
          "@type": "Question",
          "name": question,
          "acceptedAnswer": { "@type": "Answer", "text": answer }
        };
      }
      return null;
    }).filter(Boolean)
  } : null;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-4" />
        <p className={`text-xl transition-colors duration-300 ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
        }`}>Loading article...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center px-4 ${
        theme === 'dark' ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'
      }`}>
        <p className="text-6xl mb-4">😕</p>
        <h1 className="text-2xl font-bold mb-4">{error}</h1>
        <Link
          to="/blog"
          className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition"
        >
          <ArrowLeft className="w-5 h-5" /> Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <>
      {post && (
        <SEO
          title={post.seoTitle || post.title}
          description={post.seoDescription || post.excerpt}
          canonicalUrl={post.canonicalUrl || shareUrl}
          image={post.image || `${SITE_URL}/logo512.png`}
          type="article"
          author={post.author}
          publishedTime={post.date}
          modifiedTime={post.updatedAt || post.date}
          section={post.category}
          tags={post.tags}
          url={`/blog/${slug}`}
          breadcrumbs={[
            { name: "Home", url: "/" },
            { name: "Blog", url: "/blog" },
            { name: post.title, url: `/blog/${slug}` }
          ]}
          extraStructuredDataArray={[
            blogPostSchema,
            ...(faqSchema && faqSchema.mainEntity.length > 0 ? [faqSchema] : []),
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
                { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${SITE_URL}/blog` },
                { "@type": "ListItem", "position": 3, "name": post.title, "item": shareUrl }
              ]
            }
          ]}
        />
      )}

      <article className={`min-h-screen transition-all duration-500 overflow-hidden pb-20 ${
        theme === 'dark' ? 'bg-black' : 'bg-gray-50/50'
      }`}>

        {/* Full-bleed Hero Image */}
        <div className="relative min-h-[480px] md:min-h-[560px] w-full overflow-hidden flex items-end pt-28 md:pt-36">
          {post?.image ? (
            <img
              src={post.image}
              alt={post.title}
              className="absolute inset-0 w-full h-full object-cover scale-102 filter brightness-[0.75] transition-transform duration-[1200ms]"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-black" />
          )}

          {/* Gradient overlay */}
          <div className={`absolute inset-0 transition-colors duration-500 ${
            theme === 'dark'
              ? 'bg-gradient-to-t from-black via-black/60 to-black/5'
              : 'bg-gradient-to-t from-gray-900/90 via-gray-900/50 to-transparent'
          }`} />

          {/* Ambient glow */}
          <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-red-600/10 rounded-full blur-[120px] pointer-events-none" />

          {/* Hero Content */}
          <div className="max-w-4xl mx-auto w-full px-6 pb-24 md:pb-36 relative z-10">
            {/* Breadcrumb */}
            <nav className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wider mb-6 transition-colors duration-300 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-300'
            }`}>
              <Link to="/" className="hover:text-red-400 transition">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/blog" className="hover:text-red-400 transition">Blog</Link>
              <ChevronRight className="w-3 h-3" />
              <span className={theme === 'dark' ? 'text-gray-300' : 'text-white'}>
                {post?.title?.substring(0, 30)}{post?.title?.length > 30 ? '...' : ''}
              </span>
            </nav>

            {/* Category + Read Time */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {post?.category && (
                <span
                  className="inline-block px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider border"
                  style={getCategoryStyle(post.category, theme)}
                >
                  {post.category}
                </span>
              )}
              {post?.readTime && (
                <span className="flex items-center gap-1.5 text-xs font-medium text-white/70">
                  <Clock className="w-3.5 h-3.5" />
                  {post.readTime}
                </span>
              )}
              <span className="flex items-center gap-1.5 text-xs text-white/70">
                <Eye className="w-3.5 h-3.5" />
                {views} views
              </span>
            </div>

            {/* Title */}
            <h1 className={`text-3xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight mb-6 article-headline ${
              theme === 'dark' ? 'text-white' : 'text-white'
            }`}>
              {post?.title}
            </h1>

            {/* Excerpt */}
            <p className={`text-lg md:text-xl mb-6 leading-relaxed max-w-3xl article-excerpt ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-200'
            }`}>
              {post?.excerpt}
            </p>

            {/* Meta */}
            <div className={`flex flex-wrap items-center gap-5 text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-300'
            }`}>
              <span className="flex items-center gap-2">
                <User className="w-4 h-4 text-red-400" />
                <span className="font-semibold text-white">{post?.author}</span>
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-red-400" />
                {formatDate(post?.date)}
              </span>
            </div>
          </div>
        </div>

        {/* Main content area: sidebar + article */}
        <div className="max-w-7xl mx-auto px-6 relative z-20 -mt-12 md:-mt-20">
          <div className="flex gap-8 items-start">

            {/* LEFT SIDEBAR (Desktop) */}
            <aside className="hidden lg:block w-72 flex-shrink-0 sticky top-24 space-y-6">
              {/* Mobile TOC toggle (hidden on desktop) */}
              <div className="lg:hidden" />

              {/* Table of Contents */}
              {headings.length > 0 && (
                <TableOfContents headings={headings} theme={theme} activeId={activeHeadingId} />
              )}

              {/* Author Card */}
              <AuthorCard author={post?.author} theme={theme} />

              {/* Share buttons sidebar */}
              <div className={`rounded-2xl p-5 ${
                theme === 'dark' ? 'bg-white/5 border border-white/5' : 'bg-gray-50 border border-gray-100'
              }`}>
                <h3 className={`text-xs font-extrabold uppercase tracking-widest mb-4 flex items-center gap-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <Send className="w-4 h-4" /> Share This Article
                </h3>
                <div className="flex flex-col gap-2">
                  {[
                    { label: 'Facebook', icon: <Facebook className="w-4 h-4" />, color: 'bg-blue-600 hover:bg-blue-700', action: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank') },
                    { label: 'Twitter', icon: <Twitter className="w-4 h-4" />, color: 'bg-sky-500 hover:bg-sky-600', action: () => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`, '_blank') },
                    { label: 'LinkedIn', icon: <Linkedin className="w-4 h-4" />, color: 'bg-blue-700 hover:bg-blue-800', action: () => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`, '_blank') },
                    { label: 'Email', icon: <Mail className="w-4 h-4" />, color: theme === 'dark' ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-500 hover:bg-gray-600', action: () => window.location.href = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareUrl)}` },
                  ].map(item => (
                    <button
                      key={item.label}
                      onClick={item.action}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-300 ${item.color}`}
                    >
                      {item.icon} {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* MOBILE TOC TOGGLE */}
            {headings.length > 0 && (
              <div className="lg:hidden w-full mb-4">
                <button
                  onClick={() => setTocOpen(!tocOpen)}
                  className={`w-full flex items-center justify-between px-5 py-3 rounded-2xl border text-sm font-semibold transition-all ${
                    theme === 'dark'
                      ? 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <List className="w-4 h-4" /> Table of Contents
                  </span>
                  {tocOpen ? <X className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {tocOpen && (
                  <div className={`mt-2 rounded-2xl p-4 border ${
                    theme === 'dark' ? 'bg-black/80 border-white/10' : 'bg-white border-gray-200'
                  }`}>
                    {headings.map((h, i) => (
                      <button
                        key={i}
                        onClick={() => { scrollToHeading(h.id); setTocOpen(false); }}
                        className={`w-full text-left text-sm py-1.5 px-3 rounded-lg transition ${
                          h.level === 3 ? 'pl-6' : ''
                        } ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                      >
                        {h.text}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ARTICLE CONTENT */}
            <div className="flex-1 min-w-0">
              {/* Glass card */}
              <div className={`rounded-3xl p-6 md:p-10 lg:p-12 shadow-2xl transition-all duration-500 border ${
                theme === 'dark'
                  ? 'bg-white/[0.03] border-white/5 shadow-black/80 backdrop-blur-2xl'
                  : 'bg-white/95 border-gray-200/60 shadow-gray-200/20 backdrop-blur-2xl'
              }`}>

                {/* Word count + read time */}
                <div className={`flex items-center gap-4 text-xs font-medium mb-8 pb-6 border-b ${
                  theme === 'dark' ? 'border-white/5 text-gray-500' : 'border-gray-100 text-gray-500'
                }`}>
                  <span className="flex items-center gap-1">
                    <BookOpen className="w-3.5 h-3.5" />
                    {wordCount.toLocaleString()} words
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {post?.readTime}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Updated {formatDate(post?.updatedAt || post?.date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Tag className="w-3.5 h-3.5" />
                    {post?.category}
                  </span>
                </div>

                {/* Article body */}
                <div
                  className={`prose max-w-none transition-colors duration-500 blog-prose ${
                    theme === 'dark'
                      ? 'prose-invert prose-headings:text-white prose-p:text-gray-300 prose-strong:text-white prose-li:text-gray-300 prose-a:text-red-400'
                      : 'prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-li:text-gray-700 prose-a:text-red-600'
                  }`}
                  dangerouslySetInnerHTML={{ __html: processedContent }}
                />

                {/* Tags */}
                {post?.tags && post.tags.length > 0 && (
                  <div className={`mt-12 pt-8 border-t ${
                    theme === 'dark' ? 'border-white/5' : 'border-gray-100'
                  }`}>
                    <h3 className={`text-sm font-extrabold uppercase tracking-wider mb-4 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-800'
                    }`}>Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag, index) => (
                        <span
                          key={index}
                          className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-300 ${
                            theme === 'dark'
                              ? 'bg-white/5 text-gray-400 hover:bg-red-950/20 hover:text-red-400 border border-transparent'
                              : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600 border border-transparent'
                          }`}
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* About the Author */}
                <div className={`mt-12 rounded-2xl p-8 border ${
                  theme === 'dark'
                    ? 'bg-gradient-to-br from-red-950/20 to-transparent border-red-900/20'
                    : 'bg-gradient-to-br from-red-50/50 to-transparent border-red-100'
                }`}>
                  <div className="flex items-start gap-5">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-red-500 via-red-600 to-pink-600 flex items-center justify-center text-white text-2xl font-extrabold flex-shrink-0 shadow-xl shadow-red-600/20">
                      {post?.author ? post.author.charAt(0).toUpperCase() : 'Q'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`font-bold text-lg ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {post?.author || 'Qingshun Engineering Team'}
                        </h3>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-600 border border-red-200">
                          Author
                        </span>
                      </div>
                      <p className={`text-sm font-semibold text-red-600 mb-2`}>
                        Connector Manufacturing Experts · Qingshun Electronics
                      </p>
                      <p className={`text-sm leading-relaxed ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        The engineering team at Qingshun Electronics brings 14+ years of hands-on experience in connector design, injection molding, and precision manufacturing. We write to help procurement engineers and sourcing professionals make better decisions about connector sourcing and supply chain management.
                      </p>
                      <div className="flex items-center gap-4 mt-3">
                        <Link
                          to="/contact"
                          className="inline-flex items-center gap-1.5 text-sm font-bold text-red-600 hover:text-red-700 transition-colors"
                        >
                          <Mail className="w-4 h-4" /> Contact
                        </Link>
                        <Link
                          to="/about"
                          className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
                            theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <Users className="w-4 h-4" /> About Us
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Share buttons */}
                <div className={`mt-10 pt-8 border-t ${
                  theme === 'dark' ? 'border-white/5' : 'border-gray-100'
                }`}>
                  <h3 className={`text-sm font-extrabold uppercase tracking-wider mb-4 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-800'
                  }`}>
                    Share this article
                  </h3>
                  <ShareButtons url={shareUrl} title={shareTitle} theme={theme} />
                </div>

                {/* CTA Box */}
                <div className="mt-12 p-8 md:p-10 rounded-2xl bg-gradient-to-br from-red-600 via-red-700 to-pink-700 text-white text-center shadow-xl relative overflow-hidden">
                  <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-3xl pointer-events-none" />
                  <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-pink-400/10 rounded-full blur-2xl pointer-events-none" />

                  <div className="relative z-10">
                    <div className="text-4xl mb-3">🏭</div>
                    <h3 className="text-2xl md:text-3xl font-extrabold mb-3">
                      Need a Custom Connector Manufacturer?
                    </h3>
                    <p className="text-base mb-8 opacity-90 max-w-lg mx-auto leading-relaxed">
                      Qingshun Electronics manufactures precision connectors, cable assemblies &amp; stamped parts at 50–70% less than OEM brands. ISO 9001:2015 certified. Free samples available.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Link
                        to="/contact"
                        className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-red-700 font-extrabold rounded-xl hover:bg-gray-100 hover:scale-[1.03] transition transform shadow-lg shadow-black/10"
                      >
                        Get a Free Quote <ArrowRight className="w-4 h-4" />
                      </Link>
                      <Link
                        to="/services"
                        className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white/10 text-white font-bold rounded-xl hover:bg-white/20 transition border border-white/20"
                      >
                        View Our Services
                      </Link>
                    </div>
                    <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs opacity-80">
                      <span>✓ ISO 9001:2015</span>
                      <span>✓ Free Samples</span>
                      <span>✓ 14+ Years Experience</span>
                    </div>
                  </div>
                </div>

                {/* Related Articles */}
                {relatedPosts.length > 0 && (
                  <div className={`mt-14 pt-10 border-t ${
                    theme === 'dark' ? 'border-white/5' : 'border-gray-100'
                  }`}>
                    <div className="flex items-center justify-between mb-8">
                      <h2 className={`text-xl font-extrabold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        Related Articles
                      </h2>
                      <Link
                        to="/blog"
                        className={`text-sm font-semibold flex items-center gap-1.5 transition-colors ${
                          theme === 'dark' ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'
                        }`}
                      >
                        View All <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      {relatedPosts.map((rp) => (
                        <RelatedPostCard key={rp.id} post={rp} theme={theme} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Comments Section */}
                <div className={`mt-14 pt-10 border-t ${
                  theme === 'dark' ? 'border-white/5' : 'border-gray-100'
                }`}>
                  <div className="flex items-center gap-3 mb-6">
                    <MessageSquare className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                    <h2 className={`text-xl font-extrabold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Comments &amp; Discussion
                    </h2>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                      theme === 'dark' ? 'bg-white/5 text-gray-500' : 'bg-gray-100 text-gray-500'
                    }`}>
                      Coming Soon
                    </span>
                  </div>

                  <div className={`rounded-2xl p-8 text-center ${
                    theme === 'dark' ? 'bg-white/5 border border-dashed border-white/10' : 'bg-gray-50 border border-dashed border-gray-200'
                  }`}>
                    <MessageSquare className={`w-12 h-12 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`} />
                    <p className={`text-base font-semibold mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Comments are coming soon
                    </p>
                    <p className={`text-sm mb-5 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                      Have questions about this article? Get in touch with our engineering team directly.
                    </p>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold rounded-xl hover:from-red-700 hover:to-red-800 transition shadow-lg shadow-red-600/20"
                    >
                      <Mail className="w-4 h-4" /> Contact Engineering Team
                    </Link>
                  </div>
                </div>

                {/* Back to Blog */}
                <div className="mt-10 text-center">
                  <Link
                    to="/blog"
                    className={`inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider transition ${
                      theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to Knowledge Hub
                  </Link>
                </div>

                {/* Related Services */}
                <div className={`mt-10 pt-8 border-t ${
                  theme === 'dark' ? 'border-white/5' : 'border-gray-100'
                }`}>
                  <h3 className={`text-sm font-extrabold uppercase tracking-wider mb-5 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-800'
                  }`}>Our Manufacturing Capabilities</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      { name: 'Connector Manufacturing', url: '/services', icon: '🔌' },
                      { name: 'Stamping Services', url: '/services', icon: '🔨' },
                      { name: 'Mold Development', url: '/services', icon: '⚙️' },
                      { name: 'Injection Molding', url: '/manufacturing', icon: '⚡' },
                      { name: 'Cable Assembly', url: '/services', icon: '🔗' },
                      { name: 'Quality Control', url: '/manufacturing', icon: '✅' },
                      { name: 'CNC Machining', url: '/manufacturing', icon: '🔧' },
                      { name: 'Assembly Lines', url: '/manufacturing', icon: '🏭' },
                      { name: 'Get a Quote', url: '/contact', icon: '📧' },
                    ].map((item, i) => (
                      <Link
                        key={i}
                        to={item.url}
                        className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                          theme === 'dark'
                            ? 'bg-white/5 text-gray-400 hover:bg-red-950/20 hover:text-red-400 border border-transparent'
                            : 'bg-gray-50 text-gray-600 hover:bg-red-50 hover:text-red-600 border border-gray-100'
                        }`}
                      >
                        <span>{item.icon}</span> {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

export default BlogPost;
